import React, { useEffect, useState } from 'react';
import API from '../../Api';
import { useAuth } from '../../context/AuthContext';

export default function SettingsBilling() {
  const { startCheckout, refreshUsage, refreshSubscription, usage } = useAuth();
  const [billing, setBilling] = useState(null);
  const [org, setOrg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Sequential on purpose: billing/org/usage GETs previously provisioned in parallel
        // and locked the users row, which hung Settings and then login.
        await API.post('/api/billing/refresh', {}, { timeout: 20000 }).catch(() => {});
        const billingRes = await API.get('/api/billing/current', { timeout: 15000 });
        if (cancelled) return;
        setBilling(billingRes.data);

        const orgRes = await API.get('/api/organization', { timeout: 15000 });
        if (cancelled) return;
        setOrg(orgRes.data);

        const plansRes = await API.get('/api/billing/plans', { timeout: 15000 });
        if (cancelled) return;
        if (Array.isArray(plansRes.data?.plans) && plansRes.data.plans.length) {
          setPlans(plansRes.data.plans);
          const current = String(billingRes.data?.plan || '').toLowerCase();
          const match = plansRes.data.plans.find((plan) => plan.id === current);
          setSelectedPlan(match ? match.id : plansRes.data.plans[0].id);
        }

        await refreshUsage();
        if (cancelled) return;
        await refreshSubscription();
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load billing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUsage, refreshSubscription]);

  const focusPlanSelection = (message) => {
    setError('');
    setNotice(message || 'This account is on the Free plan. Choose a paid plan to start checkout.');
    document.getElementById('change-plan')?.focus();
  };

  const reconcile = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await API.post('/api/billing/reconcile');
      const billingRes = await API.get('/api/billing/current', { timeout: 15000 });
      setBilling(billingRes.data);
      if (res.data?.requiresPlanSelection) {
        focusPlanSelection(res.data.message);
        return;
      }
      if (res.data?.requiresCheckout) {
        await startCheckout(selectedPlan);
        return;
      }
      if (!res.data?.recovered) {
        setError(res.data?.message || "We couldn't verify your billing account. Your account data is safe. Please retry or contact support if the problem continues.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "We couldn't verify your billing account. Your account data is safe. Please retry or contact support if the problem continues.");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    if (!billing?.hasStripeCustomer) {
      focusPlanSelection();
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await API.post('/api/billing/portal-session');
      if (res.data?.requiresPlanSelection) {
        focusPlanSelection(res.data.message);
        return;
      }
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError('Billing portal did not return a URL');
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to open Stripe billing portal');
    } finally {
      setBusy(false);
    }
  };

  const changePlan = async () => {
    const activePlan = String(billing?.plan || '').toLowerCase();
    if (activePlan && selectedPlan === activePlan && String(billing?.status || '').toLowerCase() === 'active') {
      setError('This plan is already active.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await startCheckout(selectedPlan);
      if (result?.message && !result?.url && result?.outcome !== 'updated') {
        setError(result.message);
      }
      const billingRes = await API.get('/api/billing/current', { timeout: 15000 });
      setBilling(billingRes.data);
      await refreshSubscription();
    } catch (err) {
      setError(err.message || 'Unable to change plan');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="settings-panel">Loading billing…</div>;
  }

  const planLabel = (billing?.planLabel || billing?.plan || 'free').toUpperCase();
  const currentPlanId = String(billing?.plan || '').toLowerCase();
  const sameActivePlan = selectedPlan === currentPlanId && String(billing?.status || '').toLowerCase() === 'active';
  const seatsUsed = usage?.currentSeats ?? org?.seatsUsed ?? '—';
  const maxSeats = usage?.maxSeats ?? billing?.maxSeats ?? org?.maxSeats ?? '—';
  const usedGb = usage?.bytesIngestedGb ?? 0;
  const limitGb = usage?.dailyLimitGb ?? billing?.dailyIngestGb ?? '∞';

  return (
    <div className="settings-panel">
      <h2>Billing & Plan</h2>
      <p className="settings-subtitle">Subscription, usage, and Stripe billing management.</p>
      {billing?.billingUnverified && (
        <div className="settings-alert settings-alert--error">
          {billing.billingMessage || "We couldn't verify your billing account. Your account data is safe. Please retry or contact support if the problem continues."}
        </div>
      )}
      {notice && <div className="settings-alert">{notice}</div>}
      {error && <div className="settings-alert settings-alert--error">{error}</div>}

      <div className="settings-field">
        <label>Current Plan</label>
        <div className="settings-readonly">
          <strong style={{ fontSize: 22 }}>{planLabel}</strong>
          <div style={{ marginTop: 8, color: '#4b5563' }}>
            {maxSeats} users · {limitGb == null ? 'Unlimited' : `${limitGb} GB/day`} ·{' '}
            {billing?.retentionDays || org?.retentionDays || '—'}-day retention
          </div>
        </div>
      </div>

      <div className="settings-divider" />
      <h2 style={{ fontSize: 20 }}>Usage</h2>
      <div className="settings-stat-row">
        <div>
          <strong>Users</strong>
          <div>
            {seatsUsed} / {maxSeats}
          </div>
        </div>
        <div>
          <strong>Log ingestion</strong>
          <div>
            {usedGb} GB / {limitGb == null ? '∞' : `${limitGb} GB`} today
          </div>
        </div>
      </div>

      <div className="settings-divider" />
      <h2 style={{ fontSize: 20 }}>Subscription</h2>
      <div className="settings-field">
        <label>Status</label>
        <div className="settings-readonly">{(billing?.status || '—').toUpperCase()}</div>
      </div>
      {billing?.trialEndsAt && (
        <div className="settings-field">
          <label>Trial ends</label>
          <div className="settings-readonly">{new Date(billing.trialEndsAt).toLocaleString()}</div>
        </div>
      )}
      {billing?.cancelAtPeriodEnd && billing?.cancelEffectiveAt && (
        <div className="settings-alert">Cancellation takes effect {new Date(billing.cancelEffectiveAt).toLocaleString()}. The current plan stays active until then.</div>
      )}
      {billing?.scheduledPlan && (
        <div className="settings-alert">
          {`Your plan changes to ${String(billing.scheduledPlan).toUpperCase()}${billing.scheduledChangeAt ? ` on ${new Date(billing.scheduledChangeAt).toLocaleString()}` : ''}. The current plan stays active until then.`}
        </div>
      )}
      {billing?.duplicateSubscriptions && (
        <div className="settings-alert settings-alert--error">
          More than one active subscription was found. Your current plan was left unchanged. Contact support before changing plans.
        </div>
      )}

      <div className="settings-actions">
        {billing?.billingUnverified ? (
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            onClick={reconcile}
            disabled={busy || !org?.canManageBilling}
          >
            {busy ? 'Checking…' : 'Retry billing setup'}
          </button>
        ) : !billing?.hasStripeCustomer ? (
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            onClick={() => focusPlanSelection()}
            disabled={busy || !org?.canManageBilling}
          >
            Choose a plan
          </button>
        ) : (
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            onClick={openPortal}
            disabled={busy || !org?.canManageBilling}
          >
            Manage Billing
          </button>
        )}
      </div>

      <div className="settings-divider" />
      <h2 style={{ fontSize: 20 }}>Change Plan</h2>
      <div className="settings-field">
        <label htmlFor="change-plan">Plan</label>
        <select
          id="change-plan"
          value={selectedPlan}
          onChange={(e) => setSelectedPlan(e.target.value)}
          disabled={!org?.canManageBilling || plans.length === 0}
        >
          {plans.length === 0 ? (
            <option value="starter">Starter</option>
          ) : (
            plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name || plan.id}
              </option>
            ))
          )}
        </select>
      </div>
      <div className="settings-actions">
        {sameActivePlan && <p className="settings-subtitle">This plan is already active.</p>}
        <button
          type="button"
          className="settings-btn settings-btn--secondary"
          onClick={changePlan}
          disabled={busy || !org?.canManageBilling || sameActivePlan}
        >
          {busy ? 'Updating…' : sameActivePlan ? 'Current plan' : 'Change Plan'}
        </button>
      </div>
    </div>
  );
}
